import useTranslation from "next-translate/useTranslation";
import { memo, useContext, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { toast } from "react-toastify";
import { AUTH_URL } from "../../../config";
import { AuthContext } from "../../../context/AuthContext";
import useModal from "../../../customHooks/useModal";
import { deleteData } from "../../../utils/deleteData";
import { postData } from "../../../utils/postData";
import AddEditCommentModal from "./AddEditCommentModal";
import DeleteCommentModal from "./DeleteCommentModal";
type Props = {
  auth_comment?: boolean;
  id: number;
  mutate: () => void;
  formattedData: any;
  setReactingCounts: any;
  isReply?: boolean;
  isAdmin?: boolean;
  commentId?: number;
};
const EditDeleteCommentDropdown = ({
  auth_comment,
  id,
  isReply,
  commentId,
  mutate,
  formattedData,
  isAdmin,
  setReactingCounts = () => null,
}: Props) => {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation("common");
  const { donor } = useContext(AuthContext);

  const {
    showModal: showAddEditCommentModal,
    handleCloseModal: handleCloseAddEditCommentModal,
    handleShowModal: handleShowAddEditCommentModal,
  } = useModal();
  const {
    showModal: showDeleteCommentModal,
    handleCloseModal: handleCloseDeleteCommentModal,
    handleShowModal: handleShowDeleteCommentModal,
  } = useModal();

  const onDeleteCommentSubmit = async () => {
    setIsLoading(true);
    try {
      await toast.promise(deleteData(`${AUTH_URL}/comments/${id}`), {
        pending: t(`loading`),
        success: `${t(`comment_deleted`)}`,
        error: "Error when fetching",
      });
      setReactingCounts((prev) => ({
        ...prev,
        comments_count: formattedData.length - 1,
      }));
      handleCloseDeleteCommentModal();
      await mutate();
    } catch (err) {
      if (err?.response?.data.status === "Unauthorized") {
        toast.error(err.response.data.status);
        setIsLoading(false);
        return;
      }

      toast.error(err?.message);
      toast.error(err.response?.data.error?.code);
    }
    setIsLoading(false);
    handleCloseDeleteCommentModal();
  };

  const onEditDropdownItemClick = () => {
    const content = !isReply
      ? formattedData?.find((comment) => comment.id === id)?.content
      : formattedData
          ?.find((comment) => commentId === comment?.id)
          .replies.find((reply) => reply?.id === id).content;

    setContent(content);
    handleShowAddEditCommentModal();
  };

  const onEditCommentSubmit = async ({ comment }) => {
    try {
      setIsLoading(true);

      await toast.promise(
        postData(`${AUTH_URL}/comments/${id}`, {
          content: comment,
        }),
        {
          pending: t(`loading`),
          success: `${t(`comment_edited`)}`,
          error: "Error when fetching",
        }
      );

      await mutate();
      handleCloseAddEditCommentModal();
      setIsLoading(false);
    } catch (err) {
      if (err?.response?.data.status === "Unauthorized") {
        toast.error(err.response.data.status);
        setIsLoading(false);
        return;
      }

      toast.error(err?.message);
      toast.error(err.response?.data.error?.code);
      setIsLoading(false);
    }
  };

  const adminNotAuth = isAdmin && !auth_comment;

  if (!donor || (!auth_comment && !isAdmin)) {
    return null;
  }

  return (
    <>
      <Dropdown
        className="btn px-0 mx-2 mx-md-0 hide-dropdown-arrow"
        drop="start"
      >
        <Dropdown.Toggle variant="none" className="p-0 " id="dropdown-basic">
          <i className={`fe-more-${"vertical"} font-size-xxl`}></i>
        </Dropdown.Toggle>
        <Dropdown.Menu as="ul" className="p-0 dropdown-menu-centered">
          <Dropdown.Item
            className="py-2 px-3"
            as="li"
            onClick={handleShowDeleteCommentModal}
          >
            <i className="fe-trash text-danger fs-lg me-1"></i>
            <span className="">{t("delete")}</span>
          </Dropdown.Item>
          {!adminNotAuth && <Dropdown.Divider as="li" className="m-0" />}
          {!adminNotAuth && (
            <Dropdown.Item
              className="py-2 px-3"
              as="li"
              onClick={onEditDropdownItemClick}
            >
              <i className="fe-edit-3 text-primary fs-lg me-1"></i>
              <span className="">{t(`edit`)}</span>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
      <AddEditCommentModal
        showAddEditCommentModal={showAddEditCommentModal}
        handleCloseAddEditCommentModal={handleCloseAddEditCommentModal}
        onEditCommentSubmit={onEditCommentSubmit}
        isLoading={isLoading}
        content={content}
        isAdd={false}
      />
      <DeleteCommentModal
        showDeleteCommentModal={showDeleteCommentModal}
        handleCloseDeleteCommentModal={handleCloseDeleteCommentModal}
        onDeleteCommentSubmit={onDeleteCommentSubmit}
        isLoading={isLoading}
        commentDetails={
          !isReply
            ? formattedData?.find((comment) => comment.id === id)?.content
            : formattedData
                ?.find((comment) => commentId === comment?.id)
                .replies.find((reply) => reply?.id === id).content
        }
      />
    </>
  );
};

export default memo(EditDeleteCommentDropdown);
